import { type ApiProblem, type ApiSchemas, call } from '@rental/contracts/client'
import { format, formatDateTime } from '@rental/i18n'
import { Button } from '@rental/ui'
import { useQueryClient } from '@tanstack/react-query'
import { Wrench } from 'lucide-react'
import { useState } from 'react'
import { can } from '../access'
import { useAccess } from '../access-context'
import { api } from '../api'
import { OutcomeNotice } from '../components/OutcomeNotice'
import { ProblemNotice } from '../components/ui'
import { useLocale } from '../i18n'
import { Thumbs } from './InspectionPanel'

type Damage = ApiSchemas['StaffDamage']

/**
 * OPS-1 on the vehicle's own page: the damage on it not yet repaired, which
 * every hand-over's diagram shows. Marking one repaired takes it off (FLT).
 */
export function VehicleDamages({
  vehicleId,
  damages,
}: {
  vehicleId: string
  damages: readonly Damage[]
}) {
  const { locale, messages: m } = useLocale()
  const { access, takeOver } = useAccess()
  const queryClient = useQueryClient()
  const [busy, setBusy] = useState<string | null>(null)
  const [problem, setProblem] = useState<ApiProblem | null>(null)
  const [done, setDone] = useState<string | null>(null)
  const manage = can(access, 'fleet.manage')

  async function repair(damage: Damage) {
    setProblem(null)
    setDone(null)
    setBusy(damage.id)
    const result = await call(
      api.POST('/v1/staff/vehicles/{vehicleId}/damages/{damageId}/repaired', {
        params: { path: { vehicleId, damageId: damage.id } },
      }),
    )
    setBusy(null)
    if (!result.ok) {
      if (!takeOver(result.problem)) setProblem(result.problem)
      return
    }
    setDone(format(m.vehicle.repaired, { description: damage.description }))
    await Promise.all(
      [['vehicle', vehicleId], ['inspection']].map((queryKey) =>
        queryClient.invalidateQueries({ queryKey }),
      ),
    )
  }

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-text-muted">{m.vehicle.damagesHint}</p>
      {damages.length === 0 ? (
        <p className="text-text-muted">{m.vehicle.noDamages}</p>
      ) : (
        <ul className="flex flex-col gap-4">
          {damages.map((damage) => (
            <li
              key={damage.id}
              className="flex flex-col gap-2 border-b border-rule pb-4 last:border-b-0 last:pb-0"
            >
              <p>
                <span className="font-medium">{m.inspection.zones[damage.zone]}</span>:{' '}
                {damage.description}
              </p>
              <p className="text-sm text-text-muted">
                {format(m.vehicle.damageFound[damage.foundAt], {
                  code: damage.foundCode,
                  date: formatDateTime(locale, damage.foundOn),
                })}
              </p>
              <Thumbs
                wide
                photos={damage.photos.filter((photo) => photo.status === 'stored')}
                alt={() =>
                  format(m.inspection.damagePhotoAlt, {
                    zone: m.inspection.zones[damage.zone],
                    description: damage.description,
                  })
                }
                empty={m.inspection.noPhotos}
              />
              {manage ? (
                <div>
                  <Button
                    variant="outline"
                    className="rounded-sm"
                    busy={busy === damage.id}
                    aria-label={format(m.vehicle.repairLabel, { description: damage.description })}
                    onClick={() => void repair(damage)}
                  >
                    <Wrench aria-hidden="true" className="size-4" />
                    {m.vehicle.repair}
                  </Button>
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      )}
      {/* The damage repaired leaves the list, and its button with it: the outcome takes the focus. */}
      {done ? (
        <OutcomeNotice key={done}>
          <p>{done}</p>
        </OutcomeNotice>
      ) : null}
      <ProblemNotice problem={problem} />
    </div>
  )
}
